import { Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Globe, MessageCircle, Star, CheckCircle } from "lucide-react";
import { SellerProfile } from "@/types/profiles";
import { getSellerLogoUrl } from "@/lib/utils/avatars";

interface SellerSidebarProps {
  seller: SellerProfile;
  averageRating: string;
  reviewCount: number;
  joinDate: string;
}

const SellerSidebar = ({ seller, averageRating, reviewCount, joinDate }: SellerSidebarProps) => {
  // Get the logo URL, falling back to UI Avatars if none is provided
  const logoUrl = getSellerLogoUrl(seller.logo_url, seller.name);

  return (
    <Card className="sticky top-4">
      <CardContent className="pt-6">
        <div className="flex flex-col items-center mb-4">
          <Avatar className="h-24 w-24 mb-3">
            <AvatarImage 
              src={logoUrl}
              alt={seller.name} 
            />
            <AvatarFallback>
              {seller.name.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="text-center">
            <div className="flex items-center justify-center">
              <h1 className="text-xl font-bold">{seller.name}</h1>
              {seller.verified && (
                <CheckCircle className="h-5 w-5 text-brand-blue ml-1" />
              )}
            </div>
            {reviewCount > 0 && (
              <div className="flex items-center justify-center mt-2">
                <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" />
                <span className="ml-1 font-medium">{averageRating}</span>
                <span className="text-gray-500 text-sm ml-1">({reviewCount} reviews)</span>
              </div>
            )}
            <div className="text-sm text-gray-500 mt-1">
              Joined {joinDate}
            </div>
          </div>
        </div>
        
        <Separator className="my-4" />
        
        <div className="space-y-4">
          {seller.website_url && (
            <div className="flex items-center text-sm">
              <Globe className="h-4 w-4 text-gray-500 mr-2" />
              <a 
                href={seller.website_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-brand-blue hover:underline truncate"
              >
                {seller.website_url.replace(/^https?:\/\//, '')}
              </a>
            </div>
          )}
          
          <div className="mt-4">
            <Button 
              className="w-full bg-brand-blue hover:bg-brand-darkBlue"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              Contact Seller
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SellerSidebar;
